import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { SidebarMenuButton } from '@flipdish/ui-library/components/organisms/SidebarNavigation';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import AppBar from '@mui/material/AppBar';
import { type Theme, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Translate } from 'react-localize-redux';

import { ReactComponent as FlipdishLogo } from '../../assets/images/FlipdishLogo.svg';
import { FlipdishAgentHeaderButton } from '../../components/FlipdishAgent/FlipdishAgentAssistant';
import { useHasAccessToOnboardingV3 } from '../../components/OnboardingV3/hooks/useHasAccessToOnboardingV3';
import BrandSelector from '../../components/RMS/BrandSelector/BrandSelector';
import { SupportMenu } from '../../components/SupportMenu';
import { isLocalPortalHost, isProductionPortalHost } from '../../helpers/portalHost';
import { PortalFeatureFlags } from '../../services/featureFlags/portalFeatureFlags';
import { useFeatureFlagValue } from '../../services/featureFlags/useFeatureFlagValue';
import { flagService } from '../../services/flagService/flagService';
import ErrorBoundary from './ErrorBoundary';
import PortalSearch from './Search/PortalSearch';
import TopNavMobileExpandedMenu from './TopNavMobileExpandedMenu';

type EnvironmentBannerMode = 'local' | 'devTest' | 'demoOrg';

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    backgroundColor: '#fff',
    borderBottom: 'solid 1px #dbdcdf',
    position: 'relative' as const,
  },
  appBarLocal: {
    backgroundColor: '#dceafd',
    borderBottom: 'solid 1px #dceafd',
  },
  appBarDevTest: {
    backgroundColor: '#cffad3',
    borderBottom: 'solid 1px #cffad3',
  },
  appBarDemoOrg: {
    backgroundColor: '#cffad3',
    borderBottom: 'solid 1px #cffad3',
  },
  environmentBannerLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1.5),
    color: '#1a1a1a',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  environmentBannerIcon: {
    fontSize: '1.25rem',
  },
  menuButton: {
    marginRight: theme.spacing(1.5),

    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  switch: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  holder: {
    [theme.breakpoints.down('sm')]: {
      marginLeft: 'auto',
    },
  },
  subBar: {
    display: 'flex',
    marginLeft: 'auto',
    alignItems: 'center',
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    textTransform: 'none',
  },
  icon: {
    width: '16px',
    height: '18px',
  },
  button: {
    marginLeft: theme.spacing(1.5),
  },
  searchBar: {
    width: '100%',
    maxWidth: '800px',
    [theme.breakpoints.up('md')]: {
      margin: `0px ${theme.spacing(6)}`,
    },
    [theme.breakpoints.down('md')]: {
      margin: `0px 0px 0px ${theme.spacing(2)}`,
    },
  },
}));

type Props = {
  mobileMenuOpen: boolean;
  onMobileMenuOpenChange: (open: boolean) => void;
};

function getEnvironmentBannerMode(
  isLocalHost: boolean,
  isProductionHost: boolean,
  isDemoOrg: boolean
): EnvironmentBannerMode | null {
  if (isLocalHost) {
    return 'local';
  }
  if (!isProductionHost) {
    return 'devTest';
  }
  if (isDemoOrg) {
    return 'demoOrg';
  }
  return null;
}

const environmentBannerTranslationId: Record<EnvironmentBannerMode, TranslationId> = {
  local: 'Running_locally_warning',
  devTest: 'Dev_test_env_banner',
  demoOrg: 'Demo_org_banner',
};

const TopNav = ({ mobileMenuOpen, onMobileMenuOpenChange }: Props) => {
  const { reducedOnboardingView, onboardingWaitingView } = useHasAccessToOnboardingV3();
  const isRestrictedOnboardingNav = reducedOnboardingView || onboardingWaitingView;
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  // Due to a difference in breakpoints between tailwind and mui, we need to use a custom media query to show the sidebar button at the correct width.
  const shouldShowSidebarButton = useMediaQuery('(max-width: 1024px)');
  const isShowPortalSearchOn = useAppSelector((state) =>
    flagService.isFlagOn(state, 'showPortalSearch')
  );
  const isDemoOrgFromFlag = useFeatureFlagValue(PortalFeatureFlags.demo_orgs, false);
  const currentOrg = useAppSelector((state) => state.rms.currentOrg);
  const isDemoOrg = Boolean(currentOrg?.isDemo) || isDemoOrgFromFlag;
  const environmentBannerMode = getEnvironmentBannerMode(
    isLocalPortalHost(),
    isProductionPortalHost(),
    isDemoOrg
  );
  return (
    <AppBar
      enableColorOnDark
      color="default"
      elevation={0}
      position="static"
      data-fd="top-nav"
      data-environment-banner={environmentBannerMode ?? 'none'}
      className={clsx(
        classes.appBar,
        environmentBannerMode === 'local' && classes.appBarLocal,
        environmentBannerMode === 'devTest' && classes.appBarDevTest,
        environmentBannerMode === 'demoOrg' && classes.appBarDemoOrg
      )}
    >
      <Toolbar>
        {shouldShowSidebarButton && (
          <>
            <div data-tailwind>
              <SidebarMenuButton
                isOpen={mobileMenuOpen}
                onPress={() => onMobileMenuOpenChange(!mobileMenuOpen)}
                aria-label="Open drawer"
                className={classes.menuButton}
              />
            </div>
            <div>
              <FlipdishLogo />
            </div>
          </>
        )}

        {environmentBannerMode && (
          <Typography
            component="span"
            variant="body2"
            role="status"
            className={classes.environmentBannerLabel}
            data-fd="environment-banner-label"
          >
            <SentimentSatisfiedAltIcon aria-hidden className={classes.environmentBannerIcon} />
            <Translate id={environmentBannerTranslationId[environmentBannerMode]} />
          </Typography>
        )}

        {!isRestrictedOnboardingNav && isShowPortalSearchOn && (
          <div className={classes.searchBar}>
            <ErrorBoundary identifier="PortalSearch" includeTowlie={false}>
              <PortalSearch />
            </ErrorBoundary>
          </div>
        )}

        <div className={classes.subBar}>
          {!isRestrictedOnboardingNav &&
            (isDesktop ? <SupportMenu /> : <TopNavMobileExpandedMenu />)}
          {!onboardingWaitingView && <FlipdishAgentHeaderButton />}
          <div className={classes.holder}>
            <BrandSelector />
          </div>
        </div>
      </Toolbar>
    </AppBar>
  );
};

export default TopNav;
